// In-app wizard for users setting up their first server. The reverse-WS
// architecture lets us drop Tailscale entirely — the engine dials this
// UI host directly, so the entire "share node + tailscale-up" dance
// disappears. What's left:
//
//   1. Pick a label and mint a connection token (one POST /api/vps).
//   2. Copy the prebuilt start-engine command into your server's PowerShell
//      (token + UI host URL already baked in — no manual fields).
//   3. Watch this page. When the engine completes its first dial we flip
//      a green pill and auto-enter the new row.
//
// Step 3 is load-bearing: the SPA polls the roster endpoint every 3s and
// `engine_connected` flips to true the instant the WS upgrade succeeds.
// No probe call, no port scan, no manual confirm — just observe.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vpsApi } from "../api/vps";
import type { VpsRowWithToken } from "../api/vps";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

type Step = 1 | 2 | 3;

export function OnboardingPage() {
  const { enterVps } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>(1);
  const [label, setLabel] = useState("prod");
  const [created, setCreated] = useState<VpsRowWithToken | null>(null);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => vpsApi.create({ label: label.trim() }),
    onSuccess: (row) => {
      setCreated(row);
      setStep(2);
      void qc.invalidateQueries({ queryKey: ["vps", "list"] });
    },
    onError: (e) =>
      setCreateErr(e instanceof ApiError ? e.message : "create failed"),
  });

  // Step 3: poll the roster every 3s and watch THIS row's engine_connected
  // flag. Once it flips true, auto-enter and route to /desk.
  const pollQ = useQuery({
    queryKey: ["onboarding", "poll", created?.id],
    queryFn: vpsApi.list,
    enabled: step === 3 && created !== null,
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
  });
  const liveRow = useMemo(
    () => pollQ.data?.find((r) => r.id === created?.id) ?? null,
    [pollQ.data, created],
  );
  useEffect(() => {
    if (step !== 3 || !created || !liveRow?.engine_connected) return;
    let cancelled = false;
    void (async () => {
      try {
        await enterVps(created.id);
        if (!cancelled) nav("/desk", { replace: true });
      } catch {
        // stay on the page; user can retry from the roster
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, created, liveRow?.engine_connected, enterVps, nav]);

  return (
    <div className="app-main">
      <h2>Set up your server</h2>
      <p className="muted small">
        Three short steps. No Tailscale, no port-forwarding — the engine
        dials in on its own once you start it.
      </p>

      <StepHeader step={step} setStep={(n) => created !== null || n === 1 ? setStep(n) : undefined} />

      {step === 1 && (
        <Step1Label
          label={label}
          setLabel={setLabel}
          submitting={create.isPending}
          err={createErr}
          onSubmit={() => {
            setCreateErr(null);
            create.mutate();
          }}
        />
      )}
      {step === 2 && created && (
        <Step2RunEngine
          row={created}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && created && (
        <Step3Watch
          row={created}
          connected={!!liveRow?.engine_connected}
          onBack={() => setStep(2)}
        />
      )}
    </div>
  );
}

function StepHeader({
  step,
  setStep,
}: {
  step: Step;
  setStep: (s: Step) => void;
}) {
  const labels: { n: Step; label: string }[] = [
    { n: 1, label: "Label" },
    { n: 2, label: "Run engine" },
    { n: 3, label: "Watch it connect" },
  ];
  return (
    <div className="row" style={{ gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
      {labels.map(({ n, label }) => (
        <button
          key={n}
          type="button"
          className={n === step ? "primary" : ""}
          onClick={() => setStep(n)}
          style={{ fontSize: 12 }}
          disabled={n > step + 1}
        >
          {n}. {label}
        </button>
      ))}
    </div>
  );
}

function CodeBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — user can manually select
    }
  }
  return (
    <div style={{ position: "relative", marginTop: 8 }}>
      <pre
        className="mono small"
        style={{
          background: "var(--code-bg, #1a1a1a)",
          padding: 12,
          borderRadius: 4,
          overflowX: "auto",
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {text}
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        style={{ position: "absolute", top: 6, right: 6, fontSize: 11 }}
      >
        {copied ? "Copied ✓" : "Copy"}
      </button>
    </div>
  );
}

function Step1Label({
  label,
  setLabel,
  submitting,
  err,
  onSubmit,
}: {
  label: string;
  setLabel: (s: string) => void;
  submitting: boolean;
  err: string | null;
  onSubmit: () => void;
}) {
  return (
    <div className="card">
      <h2>1. Name your server</h2>
      <p>
        Pick a short label — just something to recognize it in your roster.
        Click Mint token to generate the connection secret your engine
        needs.
      </p>
      {err && <div className="error-banner">{err}</div>}
      <label>
        <span className="label-text">Label</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="prod"
          autoFocus
        />
        <span className="label-help">
          Anything you want — "prod", "live", "amp-cash", etc.
        </span>
      </label>
      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <button
          className="primary"
          type="button"
          disabled={submitting || !label.trim()}
          onClick={onSubmit}
        >
          {submitting ? "Minting…" : "Mint token →"}
        </button>
      </div>
    </div>
  );
}

function Step2RunEngine({
  row,
  onNext,
  onBack,
}: {
  row: VpsRowWithToken;
  onNext: () => void;
  onBack: () => void;
}) {
  // The engine reads NEXUS_UI_HOST_WS + NEXUS_ENGINE_TOKEN from env (or
  // the equivalent start-engine.ps1 args). We build a single one-shot
  // PowerShell command the user pastes — both values pre-filled.
  const uiHost = uiHostWsUrl();
  const psCmd = [
    "# Run as Administrator in PowerShell on your Windows server",
    "git clone https://github.com/veasman/nexus.git C:\\Users\\Administrator\\nexus",
    "cd C:\\Users\\Administrator\\nexus",
    `.\\deploy\\start-engine.ps1 -UiHost '${uiHost}' -Token '${row.connection_token}'`,
  ].join("\n");
  return (
    <div className="card">
      <h2>2. Run the engine</h2>
      <p>
        On your Windows server, open PowerShell <strong>as Administrator</strong>
        and paste this command. First run downloads Rust and builds the
        engine (~5 min); after that it's instant.
      </p>
      <CodeBlock text={psCmd} />
      <p className="muted small" style={{ marginTop: 12 }}>
        Already cloned? Just run the last line. The script reuses your
        existing checkout and rebuilds in place.
      </p>
      <div
        className="info-banner"
        style={{ marginTop: 12, fontSize: 13 }}
      >
        <strong>Heads up:</strong> save this token now — it's the only time
        we show it. The server stores only a hash and cannot recover the
        plaintext. If you lose it, come back to the roster and click
        "Rotate token" on this row to mint a fresh one.
      </div>
      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <button type="button" onClick={onBack}>← Back</button>
        <button className="primary" type="button" onClick={onNext}>
          Engine started →
        </button>
      </div>
    </div>
  );
}

function Step3Watch({
  row,
  connected,
  onBack,
}: {
  row: VpsRowWithToken;
  connected: boolean;
  onBack: () => void;
}) {
  return (
    <div className="card">
      <h2>3. Watch it connect</h2>
      <p>
        We're polling every 3 seconds. When your engine completes its first
        dial-out, the pill below flips green and we'll drop you straight
        into the desk view.
      </p>
      <div className="row" style={{ marginTop: 12, gap: 8, alignItems: "center" }}>
        <span className={connected ? "pill ghost" : "pill dim"}>
          {connected ? "✓ connected — entering" : "waiting for engine…"}
        </span>
        <span className="muted small">
          Label: <code>{row.label}</code>
        </span>
      </div>
      {!connected && (
        <p className="muted small" style={{ marginTop: 12 }}>
          Still red? On the server, check that <code>start-engine.ps1</code>
          is running and the console shows <code>reverse-ws dialer enabled</code>.
          If not, the token is probably wrong — back up and re-copy.
        </p>
      )}
      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <button type="button" onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}

// Derive the wss:// URL the engine should dial. Same origin as the SPA,
// scheme swapped from http(s) to ws(s), path `/_engine`.
function uiHostWsUrl(): string {
  if (typeof window === "undefined") return "wss://example.com/_engine";
  const loc = window.location;
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${loc.host}/_engine`;
}
